import React, { useState } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CohortList } from '@/components/Hypertracker/HyperSearch/HyperSearchStyled';
import UnifiedSearchRow from './UnifiedSearchRow';
import { SearchResultEntry } from './unifiedSearchTypes';

interface UnifiedSearchDropdownProps {
    entries: SearchResultEntry[];
    searchText: string;
    isLoadingRemote: boolean;
    focusedIndex: number;
    setFocusedIndex: React.Dispatch<React.SetStateAction<number>>;
    listRef: React.Ref<HTMLUListElement>;
    inputRef?: React.Ref<HTMLInputElement>;
    onBlur?: (arg: React.Ref<HTMLUListElement> | React.Ref<HTMLInputElement>) => void;
    onResultClick: (entry: SearchResultEntry) => void;
}

const UnifiedSearchDropdown: React.FC<UnifiedSearchDropdownProps> = ({
    entries,
    searchText,
    isLoadingRemote,
    focusedIndex,
    setFocusedIndex,
    listRef,
    inputRef,
    onBlur,
    onResultClick,
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'hypersearch' });
    const [isListHovered, setIsListHovered] = useState(false);

    const handleListKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setFocusedIndex((prevIndex) =>
                prevIndex < entries.length - 1 ? prevIndex + 1 : prevIndex,
            );
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setFocusedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (focusedIndex !== -1 && entries[focusedIndex]) {
                onResultClick(entries[focusedIndex]);
            }
        } else if (event.key === 'Escape' && inputRef && onBlur) {
            event.preventDefault();
            onBlur(inputRef);
        }
    };

    const onBlurList = () => {
        if (inputRef && onBlur) {
            onBlur(inputRef);
        }
    };

    if (entries.length === 0 && isLoadingRemote) {
        return <CircularProgress size={16} sx={{ margin: '10px' }} color={'secondary'} />;
    }

    if (entries.length === 0) {
        return (
            <Typography variant={'p12'} color="customText.text1">
                {t('no_results')} <strong>{searchText}</strong>
            </Typography>
        );
    }

    return (
        <CohortList
            onMouseEnter={() => setIsListHovered(true)}
            onMouseLeave={() => setIsListHovered(false)}
            onBlur={onBlurList}
            ref={listRef}
            tabIndex={0}
            onKeyDown={handleListKeyDown}
        >
            {entries.map((entry, index) => (
                <UnifiedSearchRow
                    key={`${entry.group}-${entry.url}-${index}`}
                    entry={entry}
                    searchText={searchText}
                    isFocused={index === focusedIndex && !isListHovered}
                    onClick={() => onResultClick(entry)}
                />
            ))}
            {isLoadingRemote && (
                <CircularProgress size={14} sx={{ margin: '6px' }} color={'secondary'} />
            )}
        </CohortList>
    );
};

export default UnifiedSearchDropdown;
